import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

/**
 * Normalize user/locale time strings to 24h "HH:mm" for `<input type="time">`
 * and for zonedTimeToUtc.
 */
export function normalizeTimeInputToHHmm(time: string): string | null {
  const trimmed = time.trim();
  if (!trimmed) return null;

  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = Number(match24[1]);
    const minutes = Number(match24[2]);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    return null;
  }

  const match12 = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let hours = Number(match12[1]);
    const minutes = Number(match12[2]);
    const isPm = match12[3].toUpperCase() === "PM";
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
    if (hours === 12) hours = isPm ? 12 : 0;
    else if (isPm) hours += 12;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  return null;
}

/** UTC ISO timestamp → local "HH:mm" for time inputs. */
export function utcIsoToTimeInputValue(
  isoUtc: string,
  timezone: string
): string {
  if (!isoUtc) return "";
  try {
    return formatInTimeZone(new Date(isoUtc), timezone, "HH:mm");
  } catch {
    return "";
  }
}

/** Local calendar date + "HH:mm" → UTC ISO (matches save payload intent). */
export function localDateAndTimeToUtcIso(
  dateStr: string,
  timeLocal: string,
  timezone: string
): string | null {
  const hhmm = normalizeTimeInputToHHmm(timeLocal);
  if (!hhmm || !dateStr) return null;
  try {
    const utc = zonedTimeToUtc(`${dateStr}T${hhmm}:00`, timezone);
    if (Number.isNaN(utc.getTime())) return null;
    return utc.toISOString();
  } catch {
    return null;
  }
}
